import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Send, AlertCircle } from "lucide-react";
import Layout from "@/components/Layout";
import FormLogo from "@/components/FormLogo";
import EmergencyToggle from "@/components/EmergencyToggle";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Section, Field, RadioGroup, CheckboxGroup, TopNotice,
  ApplicantSpocSection, RequestedBySection, SelfSkipSection, makeUploader,
  isHodDropdownOptional, stripSelfSkipTransients, ResubmitFromSection
} from "@/components/FormShell";

const BOOKING_TO = ["Students", "Staff", "HOD", "Managers", "Parents", "Guest",
  "Senior Management", "New Parents Prospects"];
const MEALS = ["Breakfast", "Lunch", "Dinner"];

export default function NewHotel() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isResubmit, setIsResubmit] = useState(false);
  const [parentRefCode, setParentRefCode] = useState(null);
  const [form, setForm] = useState({
    staffName: user?.name || "", staffEmail: user?.email || "", staffContact: "",
    mainDept: "", subDept: "", hodApproval: "", is_hod_self: undefined, is_admin1_self: undefined, admin1_self_cost: "", admin1_self_remark: "",
    // III
    purposeOfBooking: "", peopleCount: "", daysOfStay: "",
    hotelBookingTo: [],
    // IV
    checkInDate: "", checkInTime: "", checkOutDate: "", checkOutTime: "",
    room_type: "",
    // V
    country: "", state: "", city: "",
    reasonForHotel: "", hotelName: "", hotelAddress: "",
    localTaxi: "No",
    // VI
    includingFood: "No", vegOrMulti: "", mealsRequired: [],
    is_emergency: false, emergency_reason: "",
  });

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
  }, []);

  const subDepts = useMemo(() => {
    const d = depts.find((x) => x.name === form.mainDept);
    return d?.sub_departments || [];
  }, [depts, form.mainDept]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // eslint-disable-next-line no-unused-vars
  const onUpload = makeUploader(api, setAttachments, setUploading, toast, formatApiError);

  const validate = () => {
    if (!(form.is_hod_self === true || form.is_admin1_self === true) && !String(form.hodApproval || "").trim()) {
      toast.error("Please select an HOD or declare Yes on Are you a HOD? / Admin-1?");
      return false;
    }
    for (const k of ["staffName", "staffEmail", "staffContact", "mainDept", "subDept",
                      "purposeOfBooking", "peopleCount", "daysOfStay",
                     "checkInDate", "checkOutDate", "room_type", "country", "state", "city",
                     "hotelName", "hotelAddress"]) {
      if (!String(form[k] || "").trim()) { toast.error(`Please fill: ${k}`); return false; }
    }
    if (form.hotelBookingTo.length === 0) { toast.error("Select at least one category."); return false; }
    if (form.includingFood === "Yes" && form.mealsRequired.length === 0) {
      toast.error("Select at least one meal."); return false;
    }
    if (form.is_emergency && (form.emergency_reason || "").trim().length < 10) {
      toast.error("Emergency Reason is mandatory (min 10 characters).");
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/requisitions/hotel", {
        form_type: "hotel", parent_ref_code: parentRefCode || undefined, form_data: { ...stripSelfSkipTransients(form), attachments },
      });
      toast.success(`Submitted — ${data.ref_code}`);
      nav(`/requisitions/${data.ref_code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <Toaster position="bottom-right" richColors />
      <div className="max-w-5xl mx-auto relative">
        <FormLogo />
        <div className="mb-4 text-[11px] uppercase tracking-widest text-[#5a1520] font-semibold">
          VGWS-HTL-2026 · Hotel Booking
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight">
          Hotel Booking Requisition Form
        </h1>
        <p className="text-sm text-[#4a524c] mt-1">
          Vishwashanti Gurukul World School · Rajbaug, Loni Kalbhor
        </p>

        <TopNotice>
          <div className="font-serif font-semibold mb-1 flex items-center gap-2">
            <AlertCircle size={16} /> Important
          </div>
          <p>Submit this form ideally at least <strong>20 days</strong> prior to the requirement.</p>
        </TopNotice>

        <EmergencyToggle
          isEmergency={form.is_emergency}
          onToggle={(v) => setForm((f) => ({ ...f, is_emergency: v, emergency_reason: v ? f.emergency_reason : "" }))}
          emergencyReason={form.emergency_reason}
          onReasonChange={(v) => setForm((f) => ({ ...f, emergency_reason: v }))}
        />

        <form className="mt-6 space-y-6" onSubmit={onSubmit} data-testid="hotel-form">
          <ResubmitFromSection
            isResubmit={isResubmit}
            onToggle={(v) => { setIsResubmit(v); if (!v) setParentRefCode(null); }}
            parentRefCode={parentRefCode}
            onSelectHistory={(fd, ref) => {
              setForm((f) => ({ ...f, ...fd,
                staffName: f.staffName || fd.staffName || "",
                staffEmail: f.staffEmail || fd.staffEmail || "",
              }));
              setParentRefCode(ref);
            }}
          />

          <ApplicantSpocSection form={form} setField={setField} />
          <SelfSkipSection form={form} setField={setField} userRole={user?.role} />
          <RequestedBySection form={form} setField={setField} depts={depts} hods={hods} subDepts={subDepts} hodOptional={isHodDropdownOptional(form)} />

          <Section numeral="III" title="Booking details">
            <Field label="Purpose of hotel booking" required>
              <textarea className="input-vgws min-h-[70px]" required value={form.purposeOfBooking}
                        onChange={(e) => setField("purposeOfBooking", e.target.value)}
                        data-testid="field-purposeOfBooking" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="How many people" required>
                <input type="number" min="1" className="input-vgws" value={form.peopleCount}
                       onChange={(e) => setField("peopleCount", e.target.value)}
                       data-testid="field-peopleCount" />
              </Field>
              <Field label="How many days to stay" required>
                <input type="number" min="1" className="input-vgws" value={form.daysOfStay}
                       onChange={(e) => setField("daysOfStay", e.target.value)}
                       data-testid="field-daysOfStay" />
              </Field>
            </div>
            <Field label="Hotel booking to whom" required hint="Select at least one">
              <CheckboxGroup value={form.hotelBookingTo}
                             onChange={(v) => setField("hotelBookingTo", v)}
                             options={BOOKING_TO} testid="field-hotelBookingTo" />
            </Field>
          </Section>

          <Section numeral="IV" title="Stay dates">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Check-in date" required>
                <input type="date" className="input-vgws" value={form.checkInDate}
                       onChange={(e) => setField("checkInDate", e.target.value)}
                       data-testid="field-checkInDate" />
              </Field>
              <Field label="Check-in time" required>
                <input type="time" className="input-vgws" value={form.checkInTime}
                       onChange={(e) => setField("checkInTime", e.target.value)}
                       data-testid="field-checkInTime" />
              </Field>
              <Field label="Check-out date" required>
                <input type="date" className="input-vgws" value={form.checkOutDate}
                       onChange={(e) => setField("checkOutDate", e.target.value)}
                       data-testid="field-checkOutDate" />
              </Field>
              <Field label="Check-out time" required>
                <input type="time" className="input-vgws" value={form.checkOutTime}
                       onChange={(e) => setField("checkOutTime", e.target.value)}
                       data-testid="field-checkOutTime" />
              </Field>
              <Field label="Room type" required>
                <select
                  className="input-vgws"
                  value={form.room_type}
                  onChange={(e) => setField("room_type", e.target.value)}
                  data-testid="field-room_type"
                >
                  <option value="">Select room type</option>
                  <option value="Single Room">Single Room</option>
                  <option value="Double Sharing Room">Double Sharing Room</option>
                  <option value="Twin Sharing Room">Twin Sharing Room</option>
                  <option value="Triple sharing">Triple sharing</option>
                </select>
              </Field>
            </div>
          </Section>

          <Section numeral="V" title="Location & hotel">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Country" required>
                <input className="input-vgws" value={form.country}
                       onChange={(e) => setField("country", e.target.value)} data-testid="field-country" />
              </Field>
              <Field label="State" required>
                <input className="input-vgws" value={form.state}
                       onChange={(e) => setField("state", e.target.value)} data-testid="field-state" />
              </Field>
              <Field label="City" required>
                <input className="input-vgws" value={form.city}
                       onChange={(e) => setField("city", e.target.value)} data-testid="field-city" />
              </Field>
            </div>
            <Field label="Reason for specific hotel booking" required>
              <textarea className="input-vgws min-h-[60px]" value={form.reasonForHotel}
                        onChange={(e) => setField("reasonForHotel", e.target.value)}
                        data-testid="field-reasonForHotel" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Specify hotel name" required>
                <input className="input-vgws" value={form.hotelName}
                       onChange={(e) => setField("hotelName", e.target.value)}
                       data-testid="field-hotelName" />
              </Field>
              <Field label="Local taxi booking requirement" required>
                <RadioGroup name="localTaxi" value={form.localTaxi}
                            onChange={(v) => setField("localTaxi", v)}
                            options={["Yes", "No"]} testid="field-localTaxi" />
              </Field>
            </div>
            <Field label="Specify the hotel address" required>
              <textarea className="input-vgws min-h-[60px]" value={form.hotelAddress}
                        onChange={(e) => setField("hotelAddress", e.target.value)}
                        data-testid="field-hotelAddress" />
            </Field>
          </Section>

          <Section numeral="VI" title="Food preference">
            <Field label="Including food?" required>
              <RadioGroup name="includingFood" value={form.includingFood}
                          onChange={(v) => setField("includingFood", v)}
                          options={["Yes", "No"]} testid="field-includingFood" />
            </Field>
            {form.includingFood === "Yes" && (
              <>
                <Field label="Veg or multi-cuisine" required>
                  <RadioGroup name="vegOrMulti" value={form.vegOrMulti}
                              onChange={(v) => setField("vegOrMulti", v)}
                              options={["Veg", "Multi Cuisine"]} testid="field-vegOrMulti" />
                </Field>
                <Field label="Food requirement — select all that apply" required hint="Select at least one meal">
                  <CheckboxGroup value={form.mealsRequired}
                                 onChange={(v) => setField("mealsRequired", v)}
                                 options={MEALS} testid="field-mealsRequired" />
                </Field>
              </>
            )}
          </Section>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-xs text-[#6e7770] max-w-md">
              Fields marked <span className="text-[#b3261e]">*</span> are required.
            </p>
            <button type="submit" disabled={submitting} className="btn-vgws" data-testid="submit-hotel-button">
              <Send size={16} /> {submitting ? "Submitting…" : "Submit requisition"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
